import { Col, Row, Spin, Card, Statistic, Table, Progress, Tag } from "antd";
import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import moment from "moment";
import Page from "_components/Page";
import { companyService, materialDetailService, reportService, binService, stockReceiptService, stockIssueService, materialRequestService } from "_services";
import NumberInfo from "../home/components/NumberInfo";
import { Auth } from "_helpers";
import {
  HomeOutlined,
  InboxOutlined,
  ExportOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import WidgetCard from '_components/WidgetCard';
import logoIcon from '../../logo.svg';
import StoreMap from '_components/StoreMap';
import DashboardFlexRow from '_components/DashboardFlexRow';
import DeliveryProgress from '_components/DeliveryProgress';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function WarehouseDashboard() {
  const [loading, setLoading] = useState(false);
  const [warehouseSummary, setWarehouseSummary] = useState({});
  const [stockChartData, setStockChartData] = useState({
    labels: [],
    datasets: []
  });
  const [movementChartData, setMovementChartData] = useState({
    labels: [],
    datasets: []
  });
  const [utilizationData, setUtilizationData] = useState({
    labels: [],
    datasets: []
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [error, setError] = useState(null);
  const [widgetData, setWidgetData] = useState({
    finishedGood: { pallets: 0, packages: 0 },
    rawMaterial: { pallets: 0, packages: 0 },
  });
  const [deliveryProgressLoading, setDeliveryProgressLoading] = useState(false);
  const [deliveryProgressData, setDeliveryProgressData] = useState([]);

  // Chart options
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        display: true,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#fff',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        ticks: {
          color: '#fff',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    layout: {
      padding: {
        top: 8,
        bottom: 8,
        left: 8,
        right: 8
      }
    },
    elements: {
      bar: {
        borderWidth: 0,
      },
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        display: true,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#fff',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        ticks: {
          color: '#fff',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    layout: {
      padding: {
        top: 8,
        bottom: 8,
        left: 8,
        right: 8
      }
    },
    elements: {
      line: {
        borderWidth: 2,
      },
      point: {
        radius: 3,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        display: true,
        labels: {
          color: '#fff',
          padding: 8,
        },
      },
      title: {
        display: false,
      },
    },
    layout: {
      padding: {
        top: 8,
        bottom: 8,
        left: 8,
        right: 8
      }
    }
  };

  useEffect(() => {
    loadWarehouseData();
    loadUtilizationData('');
    fetchDeliveryProgress();
  }, []);

  // Debug useEffect to monitor utilization data
  useEffect(() => {
    console.log('Utilization data changed:', utilizationData);
  }, [utilizationData]);

  const loadWarehouseData = () => {
    setLoading(true);
    
    // Load warehouse summary data
    materialDetailService
      .getDailyStat()
      .then((data) => {
        if (data && data.content) {
          setWarehouseSummary(data.content);
        } else {
          setWarehouseSummary({});
        }
      })
      .catch((error) => {
        console.error('Error loading warehouse summary:', error);
        setError(error);
        setWarehouseSummary({});
      });

    // Load company warehouse data
    companyService.getSuggestion("?companyType=STORE").then((x) => {
      if (x && x.content && x.content.length === 1) {
        let companyId = Auth.getProfile().comp_id
          ? Auth.getProfile().comp_id
          : x.content[0].id;
        companyService.getById(companyId).then((data) => {
          if (data && data.content && data.content.addresses) {
            const storeLocationAddresses = data.content.addresses.filter(
              (item) => item.addressType === "WAREHOUSE"
            );
            const locationIds = storeLocationAddresses.map((item) => item.id).join();
            loadStockData(locationIds);
            loadMovementData(locationIds);
            loadUtilizationData(locationIds);
            loadRecentActivities(locationIds);
            loadLowStockItems(locationIds);
          } else {
            // If no addresses found, still load with empty data
            loadStockData('');
            loadMovementData('');
            loadUtilizationData('');
            loadRecentActivities('');
            loadLowStockItems('');
          }
        }).catch((error) => {
          console.error('Error loading company data:', error);
          // Load with empty data on error
          loadStockData('');
          loadMovementData('');
          loadUtilizationData('');
          loadRecentActivities('');
          loadLowStockItems('');
        });
      } else {
        // If no companies found, load with empty data
        loadStockData('');
        loadMovementData('');
        loadUtilizationData('');
        loadRecentActivities('');
        loadLowStockItems('');
      }
    }).catch((error) => {
      console.error('Error loading company suggestions:', error);
      // Load with empty data on error
      loadStockData('');
      loadMovementData('');
      loadUtilizationData('');
      loadRecentActivities('');
      loadLowStockItems('');
    });
  };

  const loadStockData = (storeLocationIds) => {
    let searchParams = `?fromDate=${encodeURIComponent(
      moment("2021-01-01").format("YYYY-MM-DD")
    )}&toDate=${moment().format("YYYY-MM-DD")}&storeLocations=${storeLocationIds}`;
    
    reportService
      .getAvailableStocks(searchParams)
      .then((data) => {
        if (data && data.content && data.content.length > 0) {
          const stockTypes = data.content.map(item => item.stockType || 'Unknown');
          const quantities = data.content.map(item => item.quantity || 0);
          const netWeights = data.content.map(item => item.netWeight || 0);

          setStockChartData({
            labels: stockTypes,
            datasets: [
              {
                label: 'Quantity',
                backgroundColor: 'rgba(54, 162, 235, 0.8)',
                borderColor: 'rgba(54, 162, 235, 1)',
                data: quantities
              },
              {
                label: 'Net Weight (Kg)',
                backgroundColor: 'rgba(255, 99, 132, 0.8)',
                borderColor: 'rgba(255, 99, 132, 1)',
                data: netWeights
              }
            ],
          });
        } else {
          // Set default empty data if no data is available
          setStockChartData({
            labels: ['No Data'],
            datasets: [
              {
                label: 'Quantity',
                backgroundColor: 'rgba(54, 162, 235, 0.8)',
                borderColor: 'rgba(54, 162, 235, 1)',
                data: [0]
              },
              {
                label: 'Net Weight (Kg)',
                backgroundColor: 'rgba(255, 99, 132, 0.8)',
                borderColor: 'rgba(255, 99, 132, 1)',
                data: [0]
              }
            ],
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error loading stock data:', error);
        // Set default empty data on error
        setStockChartData({
          labels: ['No Data'],
          datasets: [
            {
              label: 'Quantity',
              backgroundColor: 'rgba(54, 162, 235, 0.8)',
              borderColor: 'rgba(54, 162, 235, 1)',
              data: [0]
            },
            {
              label: 'Net Weight (Kg)',
              backgroundColor: 'rgba(255, 99, 132, 0.8)',
              borderColor: 'rgba(255, 99, 132, 1)',
              data: [0]
            }
          ],
        });
        setLoading(false);
      });
  };

  const loadMovementData = (storeLocationIds) => {
    // Simulate movement data for the last 7 days
    const days = [];
    const inMovements = [];
    const outMovements = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = moment().subtract(i, 'days');
      days.push(date.format('MMM DD'));
      inMovements.push(Math.floor(Math.random() * 50) + 10);
      outMovements.push(Math.floor(Math.random() * 40) + 5);
    }

    setMovementChartData({
      labels: days,
      datasets: [
        {
          label: 'Stock In',
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          data: inMovements,
          tension: 0.1
        },
        {
          label: 'Stock Out',
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          data: outMovements,
          tension: 0.1
        }
      ],
    });
  };

  const loadUtilizationData = (storeLocationIds) => {
    // Simulate warehouse utilization data
    const utilizationChartData = {
      labels: ['Occupied', 'Available', 'Reserved'],
      datasets: [
        {
          data: [65, 25, 10],
          backgroundColor: [
            'rgba(255, 99, 132, 0.8)',
            'rgba(54, 162, 235, 0.8)',
            'rgba(255, 205, 86, 0.8)',
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 205, 86, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
    
    console.log('Setting utilization data:', utilizationChartData);
    setUtilizationData(utilizationChartData);
  };

  const loadRecentActivities = (storeLocationIds) => {
    // Simulate recent activities
    setRecentActivities([
      {
        key: '1',
        activity: 'Stock Receipt',
        item: 'Raw Material A',
        quantity: 500,
        time: '2 hours ago',
        status: 'completed',
        user: 'John Doe'
      },
      {
        key: '2',
        activity: 'Stock Issue',
        item: 'Finished Goods B',
        quantity: 200,
        time: '4 hours ago',
        status: 'completed',
        user: 'Jane Smith'
      },
      {
        key: '3',
        activity: 'Stock Transfer',
        item: 'Component C',
        quantity: 150,
        time: '6 hours ago',
        status: 'pending',
        user: 'Mike Johnson'
      },
      {
        key: '4',
        activity: 'Stock Check',
        item: 'Material D',
        quantity: 1000,
        time: '8 hours ago',
        status: 'completed',
        user: 'Sarah Wilson'
      }
    ]);
  };

  const loadLowStockItems = (storeLocationIds) => {
    // Simulate low stock items
    setLowStockItems([
      {
        key: '1',
        item: 'Raw Material X',
        currentStock: 50,
        minStock: 100,
        supplier: 'Supplier A',
        status: 'critical'
      },
      {
        key: '2',
        item: 'Component Y',
        currentStock: 75,
        minStock: 80,
        supplier: 'Supplier B',
        status: 'warning'
      },
      {
        key: '3',
        item: 'Material Z',
        currentStock: 30,
        minStock: 50,
        supplier: 'Supplier C',
        status: 'critical'
      }
    ]);
  };

  const fetchDeliveryProgress = () => {
    setDeliveryProgressLoading(true);
    materialRequestService
      .searchSummary('?orderStages=OPEN,UPDATE,VERIFICATION,PREPARE,READY,ASSIGNED,PROCESSING')
      .then((data) => {
        const deliveries = (data.content || []).map((mrn) => {
          const hours = moment().diff(moment(mrn.requestDate), 'hours');
          return {
            mrn: mrn.requestNumber,
            timeStatus: `${hours} hr over`,
            currentStatus: mapOrderStageToStatus(mrn.orderStage),
          };
        });
        setDeliveryProgressData(deliveries);
        setDeliveryProgressLoading(false);
      })
      .catch(() => setDeliveryProgressLoading(false));
  };

  // Map API orderStage to DeliveryProgress status keys
  function mapOrderStageToStatus(orderStage) {
    switch (orderStage) {
      case 'OPEN':
      case 'UPDATE':
        return 'Open';
      case 'VERIFICATION':
        return 'Verification';
      case 'PREPARE':
        return 'Prepare';
      case 'READY':
        return 'Ready';
      case 'ASSIGNED':
        return 'Assigned';
      case 'PROCESSING':
        return 'Processing';
      default:
        return 'Open';
    }
  }

  const activityColumns = [
    {
      title: 'Activity',
      dataIndex: 'activity',
      key: 'activity',
      render: (activity) => (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          fontWeight: '500'
        }}>
          <span style={{ 
            width: '8px', 
            height: '8px', 
            borderRadius: '50%', 
            backgroundColor: activity === 'Stock Receipt' ? '#52c41a' : 
                           activity === 'Stock Issue' ? '#f5222d' : 
                           activity === 'Stock Transfer' ? '#1890ff' : '#faad14'
          }}></span>
          {activity}
        </div>
      ),
    },
    {
      title: 'Item',
      dataIndex: 'item',
      key: 'item',
      render: (item) => (
        <span style={{ 
          color: '#fff', 
          fontWeight: '500',
          fontSize: '13px'
        }}>
          {item}
        </span>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity) => (
        <span style={{ 
          color: '#1890ff', 
          fontWeight: '600',
          fontSize: '13px'
        }}>
          {typeof quantity === 'number' && quantity != null ? quantity.toLocaleString() : (quantity ?? '')}
        </span>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
      render: (time) => (
        <span style={{ 
          color: '#999', 
          fontSize: '12px',
          fontStyle: 'italic'
        }}>
          {time}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <div className="activity-status">
          <div className="activity-icon" style={{
            backgroundColor: status === 'completed' ? '#52c41a' : 
                           status === 'pending' ? '#faad14' : '#f5222d'
          }}>
            {status === 'completed' ? '✓' : status === 'pending' ? '⏳' : '✗'}
          </div>
          <Tag color={status === 'completed' ? 'green' : status === 'pending' ? 'orange' : 'red'}>
            {status.toUpperCase()}
          </Tag>
        </div>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (user) => (
        <span style={{ 
          color: '#e6e6e6', 
          fontWeight: '400',
          fontSize: '13px'
        }}>
          {user}
        </span>
      ),
    },
  ];

  const lowStockColumns = [
    {
      title: 'Item',
      dataIndex: 'item',
      key: 'item',
      render: (item) => (
        <span style={{ 
          color: '#fff', 
          fontWeight: '500',
          fontSize: '13px'
        }}>
          {item}
        </span>
      ),
    },
    {
      title: 'Current Stock',
      dataIndex: 'currentStock',
      key: 'currentStock',
      render: (currentStock) => (
        <span style={{ 
          color: '#f5222d', 
          fontWeight: '600',
          fontSize: '13px'
        }}>
          {typeof currentStock === 'number' && currentStock != null ? currentStock.toLocaleString() : (currentStock ?? '')}
        </span>
      ),
    },
    {
      title: 'Min Stock',
      dataIndex: 'minStock',
      key: 'minStock',
      render: (minStock) => (
        <span style={{ 
          color: '#faad14', 
          fontWeight: '500',
          fontSize: '13px'
        }}>
          {typeof minStock === 'number' && minStock != null ? minStock.toLocaleString() : (minStock ?? '')}
        </span>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      render: (supplier) => (
        <span style={{ 
          color: '#e6e6e6', 
          fontWeight: '400',
          fontSize: '13px'
        }}>
          {supplier}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <div className="activity-status">
          <div className="activity-icon" style={{
            backgroundColor: status === 'critical' ? '#f5222d' : '#faad14'
          }}>
            {status === 'critical' ? '⚠' : '⚡'}
          </div>
          <Tag color={status === 'critical' ? 'red' : 'orange'}>
            {status.toUpperCase()}
          </Tag>
        </div>
      ),
    },
  ];

  // Fetch widget data (replace with your real API/service)
  useEffect(() => {
    async function fetchWidgetData() {
      // Example: Replace with your actual API call
      setWidgetData({
        finishedGood: { pallets: 121, packages: 11680 },
        rawMaterial: { pallets: 277, packages: 14775 },
      });
    }
    fetchWidgetData();
  }, []);

  // Add this sample data for the store map
  const storeSections = [
    {
      name: 'B01',
      cells: [
        { code: '10-01', value: 24 },
        { code: '10-02', value: 0 },
        { code: '10-03', value: 19 },
        { code: '10-04', value: 12 },
        { code: '10-05', value: 5 },
        { code: '11-01', value: 23 },
        { code: '11-02', value: 21 },
        { code: '11-03', value: 20 },
        { code: '12-01', value: 18 },
        { code: '12-02', value: 17 },
        { code: '12-03', value: 16 },
        { code: '13-01', value: 15 },
        { code: '13-02', value: 14 },
        { code: '13-03', value: 13 },
        { code: '14-01', value: 12 },
        { code: '14-02', value: 11 },
        { code: '15-01', value: 10 },
        { code: '15-02', value: 9 },
        { code: '16-01', value: 8 },
        { code: '17-01', value: 7 },
        { code: '18-01', value: 6 },
        { code: '19-01', value: 5 },
        { code: '20-01', value: 4 },
      ],
    },
    {
      name: 'O2',
      cells: [
        { code: '10-05', value: 19 },
        { code: '11-01', value: 18 },
        { code: '12-01', value: 17 },
        { code: '13-01', value: 16 },
        { code: '14-01', value: 15 },
        { code: '15-01', value: 14 },
        { code: '16-01', value: 13 },
        { code: '17-01', value: 12 },
        { code: '18-01', value: 11 },
        { code: '19-01', value: 10 },
        { code: '20-01', value: 9 },
      ],
    },
  ];

  return (
    <Page className="warehouse-dashboard" error={error}>
      <Spin tip="Loading warehouse data..." spinning={loading}>
        <div style={{ padding: '24px', background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)', minHeight: '100vh' }}>
          <style>
            {`
              .warehouse-dashboard {
                background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
              }
              .warehouse-dashboard .ant-card {
                background: linear-gradient(145deg, #2a2a2a 0%, #1f1f1f 100%);
                border: 1px solid #404040;
                border-radius: 12px;
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
                backdrop-filter: blur(10px);
                transition: all 0.3s ease;
              }
              .warehouse-dashboard .ant-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
                border-color: #1890ff;
              }
              .warehouse-dashboard .ant-card-head {
                background: linear-gradient(145deg, #333333 0%, #262626 100%);
                border-bottom: 1px solid #404040;
                border-radius: 12px 12px 0 0;
                padding: 16px 24px;
              }
              .warehouse-dashboard .ant-card-head-title {
                color: #fff;
                font-weight: 600;
                font-size: 16px;
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
              }
              .warehouse-dashboard .ant-card-body {
                padding: 20px;
                height: calc(100% - 73px);
                overflow: hidden;
                width: 100%;
                border-radius: 0 0 12px 12px;
                display: flex;
                flex-direction: column;
              }
              .warehouse-dashboard canvas {
                max-height: 100% !important;
                max-width: 100% !important;
                width: 100% !important;
                height: 100% !important;
                display: block !important;
                border-radius: 8px;
              }
              .warehouse-dashboard .chart-container {
                position: relative;
                height: calc(100% - 40px);
                width: 100%;
                min-width: 100%;
                min-height: calc(100% - 40px);
                padding: 16px;
                background: rgba(255, 255, 255, 0.02);
                border-radius: 8px;
                border: 1px solid rgba(255, 255, 255, 0.1);
                overflow: hidden;
                display: flex;
                align-items: center;
                justify-content: center;
                flex: 1;
              }
              .warehouse-dashboard .chart-wrapper {
                width: 100%;
                height: 100%;
                position: relative;
                max-width: 100%;
                max-height: 100%;
              }
              .warehouse-dashboard .chart-inner {
                width: 100%;
                height: 100%;
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
              }
              .warehouse-dashboard .table-container {
                height: calc(100% - 40px);
                overflow: hidden;
                flex: 1;
                background: rgba(255, 255, 255, 0.02);
                border-radius: 8px;
                border: 1px solid rgba(255, 255, 255, 0.1);
                padding: 8px;
              }
              .warehouse-dashboard .ant-col {
                width: 100%;
              }
              .warehouse-dashboard .dashboard-header {
                background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
                padding: 24px;
                border-radius: 16px;
                margin-bottom: 32px;
                box-shadow: 0 8px 32px rgba(24, 144, 255, 0.3);
                border: 1px solid rgba(24, 144, 255, 0.2);
              }
              .warehouse-dashboard .metrics-row {
                margin-bottom: 32px;
              }
              .warehouse-dashboard .metric-card {
                background: linear-gradient(145deg, #2a2a2a 0%, #1f1f1f 100%);
                border: 1px solid #404040;
                border-radius: 12px;
                padding: 24px;
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
                transition: all 0.3s ease;
                position: relative;
                overflow: hidden;
              }
              .warehouse-dashboard .metric-card::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 3px;
                background: linear-gradient(90deg, #1890ff, #52c41a, #faad14, #f5222d);
              }
              .warehouse-dashboard .metric-card:hover {
                transform: translateY(-4px);
                box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
              }
              .warehouse-dashboard .metric-icon {
                width: 48px;
                height: 48px;
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 16px;
                font-size: 24px;
              }
              .warehouse-dashboard .metric-title {
                color: #999;
                font-size: 14px;
                font-weight: 500;
                margin-bottom: 8px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              .warehouse-dashboard .metric-value {
                color: #fff;
                font-size: 28px;
                font-weight: 700;
                margin-bottom: 4px;
                text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
              }
              .warehouse-dashboard .metric-subtitle {
                color: #666;
                font-size: 12px;
                font-weight: 400;
              }
              .warehouse-dashboard .charts-section {
                margin-bottom: 32px;
              }
              .warehouse-dashboard .section-title {
                color: #fff;
                font-size: 20px;
                font-weight: 600;
                margin-bottom: 24px;
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
                display: flex;
                align-items: center;
                gap: 12px;
              }
              .warehouse-dashboard .ant-table {
                background: transparent;
                border-radius: 8px;
                overflow: hidden;
              }
              .warehouse-dashboard .ant-table-thead > tr > th {
                background: linear-gradient(145deg, #333333 0%, #262626 100%);
                color: #fff;
                border-bottom: 1px solid #404040;
                font-weight: 600;
                padding: 12px 16px;
                font-size: 14px;
              }
              .warehouse-dashboard .ant-table-tbody > tr > td {
                background: rgba(255, 255, 255, 0.05);
                color: #e6e6e6;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                padding: 12px 16px;
                font-size: 13px;
              }
              .warehouse-dashboard .ant-table-tbody > tr:hover > td {
                background: rgba(24, 144, 255, 0.15);
                color: #fff;
              }
              .warehouse-dashboard .ant-table-tbody > tr:nth-child(even) > td {
                background: rgba(255, 255, 255, 0.03);
              }
              .warehouse-dashboard .ant-table-tbody > tr:nth-child(even):hover > td {
                background: rgba(24, 144, 255, 0.15);
              }
              .warehouse-dashboard .ant-tag {
                border-radius: 6px;
                font-weight: 500;
                padding: 4px 8px;
                font-size: 12px;
                border: none;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
              }
              .warehouse-dashboard .activity-status {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                font-weight: 500;
              }
              .warehouse-dashboard .activity-icon {
                width: 20px;
                height: 20px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
              }
            `}
          </style>
          
          {/* Professional Header */}
          {/* <div className="dashboard-header">
            <h1 style={{ 
              margin: 0, 
              color: '#fff', 
              fontSize: '32px', 
              fontWeight: '700',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <HomeOutlined style={{ fontSize: '36px' }} />
              Warehouse Dashboard
            </h1>
            <p style={{ 
              margin: '8px 0 0 0', 
              color: 'rgba(255, 255, 255, 0.8)', 
              fontSize: '16px',
              fontWeight: '400'
            }}>
              Real-time warehouse analytics and performance metrics
            </p>
          </div> */}

          {/* Enhanced Metrics Row */}
          <DashboardFlexRow>
            <WidgetCard
              title="FINISHED GOOD"
              titleColor="#7fffa2"
              icon={<img src={logoIcon} alt="icon" style={{ height: 28 }} />}
              iconBg="linear-gradient(135deg, #7fffa2 0%, #1890ff 100%)"
              accentColor="#7fffa2"
              value1={widgetData.finishedGood.pallets}
              subtitle1="Total Pallets"
              value2={widgetData.finishedGood.packages}
              subtitle2="Total Packages"
              valueColor="#fff"
              subtitleColor="#7fffa2"
              background="#23263a"
            />
            {/* Pie chart for FINISHED GOOD breakdown */}
            <div style={{ minWidth: 220, maxWidth: 320, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="chart-container" style={{ width: '100%', height: 180, background: 'none', boxShadow: 'none', border: 'none' }}>
                <Doughnut
                  data={{
                    labels: ['FINISHED GOOD', 'RAW MATERIAL'],
                    datasets: [
                      {
                        data: [
                          (widgetData.finishedGood.pallets || 0) + (widgetData.finishedGood.packages || 0),
                          (widgetData.rawMaterial.pallets || 0) + (widgetData.rawMaterial.packages || 0),
                        ],
                        backgroundColor: ['#7fffa2', '#00e6e6'], // match widget accent colors
                        borderColor: '#23263a',
                        borderWidth: 2,
                        hoverOffset: 16, // highlight effect
                        hoverBorderColor: '#ffe066',
                        hoverBorderWidth: 4,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: { display: true, position: 'bottom', labels: { color: '#fff', usePointStyle: true, pointStyle: 'rectRounded' } },
                    },
                    cutout: '70%',
                    responsive: true,
                    maintainAspectRatio: false,
                  }}
                />
              </div>
            </div>
            <WidgetCard
              title="RAW MATERIAL"
              titleColor="#ffe066"
              icon={<img src={logoIcon} alt="icon" style={{ height: 28 }} />}
              iconBg="linear-gradient(135deg, #ffe066 0%, #faad14 100%)"
              accentColor="#ffe066"
              value1={widgetData.rawMaterial.pallets}
              subtitle1="Total Pallets"
              value2={widgetData.rawMaterial.packages}
              subtitle2="Total Packages"
              valueColor="#fff"
              subtitleColor="#ffe066"
              background="#23263a"
            />
          </DashboardFlexRow>

          {/* StoreMap and DeliveryProgress Row */}
          <Row gutter={24} style={{ marginBottom: 32 }}>
            <Col span={12}>
              <StoreMap
                sections={storeSections}
                onFilter={() => alert('Filtering clicked!')}
              />
            </Col>
            <Col span={12}>
              <Spin spinning={deliveryProgressLoading} tip="Loading...">
                <DeliveryProgress deliveries={deliveryProgressData} />
              </Spin>
            </Col>
          </Row>

          {/* Charts Section */}
          <div className="charts-section">
            <div className="section-title">
              <span>📊</span>
              Analytics Overview
            </div>
            <DashboardFlexRow>
              <div style={{ flex: 1, minWidth: 300 }}>
                <Card title="Stock Levels by Category" style={{ height: '400px', width: '100%' }}>
                  <div className="chart-container" style={{ width: '100%' }}>
                    <div className="chart-inner">
                      {stockChartData && stockChartData.labels && stockChartData.labels.length > 0 ? (
                        <Bar data={stockChartData} options={barOptions} />
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center', 
                          height: '100%',
                          width: '100%',
                          color: '#999'
                        }}>
                          Loading stock data...
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
              <div style={{ flex: 1, minWidth: 300 }}>
                <Card title="Stock Movement Trend" style={{ height: '400px', width: '100%' }}>
                  <div className="chart-container" style={{ width: '100%' }}>
                    <div className="chart-inner">
                      {movementChartData && movementChartData.labels && movementChartData.labels.length > 0 ? (
                        <Line data={movementChartData} options={lineOptions} />
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center', 
                          height: '100%',
                          width: '100%',
                          color: '#999'
                        }}>
                          Loading movement data...
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
            </DashboardFlexRow>
            <DashboardFlexRow>
              <div style={{ flex: 1, minWidth: 220, maxWidth: 400 }}>
                <Card title="Warehouse Utilization" style={{ height: '300px', width: '100%' }}>
                  <div className="chart-container" style={{ width: '100%' }}>
                    <div className="chart-inner">
                      {utilizationData && utilizationData.labels && utilizationData.labels.length > 0 ? (
                        <Doughnut data={utilizationData} options={doughnutOptions} />
                      ) : (
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center', 
                          height: '100%',
                          width: '100%',
                          color: '#999'
                        }}>
                          Loading utilization data...
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>
              <div style={{ flex: 2, minWidth: 300 }}>
                <Card title="Recent Activities" style={{ height: '300px', width: '100%' }}>
                  <div className="table-container">
                    <Table
                      dataSource={recentActivities}
                      columns={activityColumns}
                      pagination={false}
                      size="small"
                      scroll={{ y: 180 }}
                    />
                  </div>
                </Card>
              </div>
            </DashboardFlexRow>
          </div>

          {/* Low Stock Alerts Section */}
          <div>
            <div className="section-title">
              <span>⚠️</span>
              Low Stock Alerts
            </div>
            <Row gutter={24}>
              <Col span={24}>
                <Card title="Critical Inventory Items" style={{ marginBottom: '24px', width: '100%' }}>
                  <Table
                    dataSource={lowStockItems}
                    columns={lowStockColumns}
                    pagination={false}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      </Spin>
    </Page>
  );
}

export default WarehouseDashboard; 